<template>
  <modal-default modal-title="Envio em massa" modal-id="massShipping" width="820px"
                 modal-subtitle="Cria uma anotação no funil de cada empresa selecionada"
                 @modal-closed="onClosed">
    <template>
      <!-- 1. Ação padrão + mensagem -->
      <div class="mass-shipping" v-if="!result" :key="resetKey">
        <div class="row">
          <div class="col-md-12">
            <label class="mass-shipping__label">
              Ações padrão <span>*</span>
              <small>— uma anotação por ação, em cada empresa. Vale para empresas adicionadas manualmente e para linhas da planilha sem coluna <code>acao</code>.</small>
            </label>
            <div class="mass-shipping__actions custom-scrollbar" :class="{'has-error': validate.defaultAction}">
              <label v-for="action in selectableActions" :key="action.id" class="mass-shipping__action">
                <input type="checkbox" :value="action.id" v-model="defaultActionIds"/>
                {{ action.name }}
              </label>
            </div>
            <span class="textError" v-if="validate.defaultAction">Selecione ao menos uma ação *</span>
          </div>
          <input-textarea @contentTextarea="value => defaultMsg = value" col="12"
                          placeholder="Digite seu comentário aqui" label="Mensagem padrão"/>
        </div>

        <!-- 2. Adicionar empresas: mesma entrada e mesma grade do passo 2 de Avisos e
             Campanhas. Busca e planilha passam pela API v2 (resolve-companies), que
             aceita CNPJ, id, código ou nome; não há mais lista local de empresas. -->
        <div class="row">
          <div class="col-md-12 mass-shipping__section">Adicionar empresas</div>
          <div class="col-md-9 mass-shipping__terms">
            <input type="text" v-model="terms"
                   placeholder="CNPJ, ID ou nome da empresa (separe múltiplos valores com vírgulas)"
                   :disabled="resolving"
                   @keyup.enter="resolveFromTerms">
          </div>
          <div class="col-md-3 mass-shipping__buttons">
            <button type="button" class="mass-shipping__btn mass-shipping__btn--primary"
                    :disabled="resolving" @click="resolveFromTerms">
              {{ resolving ? 'Buscando…' : 'Buscar' }}
            </button>
            <label class="mass-shipping__btn mass-shipping__btn--upload" title="Carregar planilha (.csv, .txt)">
              Planilha
              <input type="file" accept=".csv,.txt" @change="onFileSelected" :disabled="resolving"/>
            </label>
          </div>
          <div class="col-md-12 mass-shipping__hint">
            Planilha em CSV ou TXT com uma coluna <code>id</code> (id ou CNPJ da empresa); ação e mensagem vêm deste modal.
            <a href="#" @click.prevent="downloadTemplate">Baixar modelo</a>
            <span v-if="fileName">· {{ fileName }}</span>
          </div>
          <div class="col-md-12 mass-shipping__error" v-if="fileError">{{ fileError }}</div>
          <div class="col-md-12 mass-shipping__error" v-if="searchError">{{ searchError }}</div>
          <div class="col-md-12 mass-shipping__error" v-if="notFound.length">
            Sem correspondência ({{ notFound.length }}): {{ notFound.join(', ') }}
          </div>
          <div class="col-md-12 mass-shipping__error" v-if="notBrazil.length">
            {{ notBrazil.length }} empresa(s) fora do Brasil não vieram na busca:
            {{ notBrazil.map(companyLabel).join(', ') }}
          </div>
          <div class="col-md-12 mass-shipping__error" v-if="shardsFailed.length">
            <strong>Resultado incompleto.</strong> Bancos sem resposta: {{ shardsFailed.join(', ') }}.
            Empresas desses bancos podem estar faltando. Refaça a busca antes de enviar.
          </div>

          <!-- Grade: resultado da busca + empresas já selecionadas (autocomplete, planilha).
               Marcar/desmarcar altera a seleção; a lixeira tira da grade e do campo de busca. -->
          <div class="col-md-12" v-if="rows.length">
            <div class="mass-shipping__chips">
              <span>Empresas selecionadas <b>{{ selection.length }}</b></span>
              <a href="#" @click.prevent="clearSelection">Limpar seleção</a>
            </div>
            <div class="mass-shipping__grid">
              <company-selection-table :rows="rows" :selected="selectedRows"
                                       @update:selected="onGridSelection" @remove="removeRow"/>
            </div>
          </div>
          <div class="col-md-12 mass-shipping__empty" v-else-if="searched">
            Nenhuma empresa encontrada para os termos informados.
          </div>
        </div>

        <!-- 3. Resumo: a grade acima já mostra as empresas; aqui só o total e os avisos. -->
        <div class="row">
          <div class="col-md-12 mass-shipping__section">
            {{ selection.length }} empresa(s) selecionada(s), {{ totalItems }} anotação(ões)
            <span class="mass-shipping__warn" v-if="missingActionCount">
              · {{ missingActionCount }} sem ação (marque ao menos uma ação padrão)
            </span>
          </div>

          <div class="col-md-12 mass-shipping__unresolved" v-if="unresolved.length">
            <strong>Não resolvidos ({{ unresolved.length }})</strong>
            <a href="#" @click.prevent="unresolved = []">limpar</a>
            <ul>
              <li v-for="(u, index) in unresolved" :key="index">
                Linha {{ u.rowNumber }}: <code>{{ u.value || '(vazio)' }}</code> — {{ u.reason }}
              </li>
            </ul>
          </div>
        </div>

        <!-- 4. Enviar -->
        <div class="row">
          <div class="col-md-8 mass-shipping__progress" v-if="loadingSend">
            Enviando lote {{ progress.current }}/{{ progress.total }}…
            <div class="mass-shipping__bar"><span :style="{width: progressPercent + '%'}"></span></div>
          </div>
          <btn-search @emit-click="send()" have-loading="true" :loading="loadingSend" label="Enviar"
                      :disabled="!canSend" col="3"/>
        </div>
      </div>

      <!-- 5. Resultado -->
      <div class="mass-shipping" v-else>
        <div class="mass-shipping__summary">
          <p><strong>{{ result.created }}</strong> anotação(ões) criada(s), <strong>{{ result.failed.length }}</strong> falha(s).</p>
          <ul v-if="result.failed.length">
            <li v-for="(f, index) in result.failed" :key="index">
              {{ companyName(f.companyId) }} (<code>{{ f.companyId }}</code>)
              <span v-if="f.businessNoteType !== null && f.businessNoteType !== undefined">· {{ actionName(f.businessNoteType) }}</span>
              — {{ f.reason }}
            </li>
          </ul>
        </div>
        <btn-search @emit-click="close()" label="Fechar" col="3"/>
      </div>
    </template>
  </modal-default>
</template>

<script>
import ModalDefault from "../components/modals/modalDefault.vue";
import InputTextarea from "../components/inputs/inputTextarea.vue";
import BtnSearch from "../components/buttons/btnSearch.vue";
import CompanySelectionTable from "../restricted/broadcast/companySelectionTable.vue";
import {BROADCAST_RESOLVE_COMPANIES, POST_REPORT_FUNIL} from "../../../urlConfig";
import {REQUEST_SERVICE, UTIL_SERVICE} from "../../../requestConfig";
import {ALERT_SERVICE} from "../../../msgAlertService";
import {isNoCrossToken, requestApiV2} from "../restricted/broadcast/broadcastApiV2Auth";
import {splitCompanyTerms} from "../restricted/broadcast/broadcastCsv";
import {
  readMassShippingCsv,
  resolveCsvRows,
  mergeSelection,
  mergeRows,
  rowFromSelection,
  applyGridSelection,
  termsWithoutCompany,
  chunk,
  MASS_SHIPPING_CHUNK_SIZE,
  downloadMassShippingTemplate
} from "../restricted/funnel/massShippingCsv";

export default {
  name: "modalMassShipping",
  components: {ModalDefault, InputTextarea, BtnSearch, CompanySelectionTable},
  props: {
    listAction: {type: Array, required: true},
    createdBy: {type: String, default: ''}
  },
  data() {
    return {
      defaultActionIds: [],
      defaultMsg: '',
      selection: [],
      unresolved: [],
      // Busca em lote (grade de empresas)
      terms: '',
      rows: [],
      notFound: [],
      notBrazil: [],
      shardsFailed: [],
      searched: false,
      searchError: '',
      resolving: false,
      fileName: '',
      fileError: '',
      validate: {defaultAction: false},
      loadingSend: false,
      progress: {current: 0, total: 0},
      result: null,
      sent: false,
      resetKey: 0
    }
  },
  computed: {
    selectableActions() {
      return this.listAction.filter(a => a.id !== '' && a.id !== null && a.id !== undefined);
    },
    defaultActions() {
      return this.selectableActions.filter(a => this.defaultActionIds.indexOf(a.id) !== -1);
    },
    missingActionCount() {
      return this.selection.filter(item => !this.effectiveActions(item).length).length;
    },
    totalItems() {
      return this.selection.reduce((sum, item) => sum + this.effectiveActions(item).length, 0);
    },
    canSend() {
      return this.selection.length > 0 && this.missingActionCount === 0 && !this.loadingSend;
    },
    progressPercent() {
      return this.progress.total ? Math.round(100 * this.progress.current / this.progress.total) : 0;
    },
    // Linhas da grade que estão na seleção. Derivado de `rows` (e não da seleção)
    // para a chave contextCode:companyId da tabela bater nos dois lados.
    selectedRows() {
      const selected = {};
      this.selection.forEach(item => { selected[String(item.companyId)] = true; });
      return this.rows.filter(row => selected[String(row.companyId)]);
    }
  },
  watch: {
    defaultActionIds() {
      if (this.defaultActions.length) this.validate.defaultAction = false;
    },
    // Toda empresa selecionada (autocomplete, planilha, busca) aparece na grade.
    selection(items) {
      this.rows = mergeRows(this.rows, items.map(rowFromSelection));
    }
  },
  methods: {
    open() {
      $('#massShipping').modal();
    },
    close() {
      $('#massShipping').modal('hide');
    },
    onClosed() {
      // modal-default emite modal-closed no hidden.bs.modal; mantém o refresh do funil após envio.
      if (this.sent) {
        this.$emit('done');
      }
    },
    // Linha da planilha com ação própria → só ela; senão, todas as ações padrão marcadas.
    effectiveActions(item) {
      return item.action ? [item.action] : this.defaultActions;
    },
    companyLabel(row) {
      return row.name || row.cnpj || row.companyId;
    },
    resolveFromTerms() {
      const terms = splitCompanyTerms(this.terms);
      if (!terms.length) return;
      this.resolveCompanies(terms).then((data) => {
        if (data) this.applySearchResult(data);
      });
    },
    // Mesma rota do passo 2 de Avisos e Campanhas: uma única regra de casamento
    // (CNPJ com ou sem pontuação, id, código ou nome). Resolve com os dados da
    // API, ou com null quando falhou (o erro já foi mostrado em searchError).
    resolveCompanies(terms) {
      if (!terms.length || this.resolving) return Promise.resolve(null);

      this.resolving = true;
      this.searchError = '';

      return requestApiV2('POST', BROADCAST_RESOLVE_COMPANIES, {terms: terms}).then((response) => {
        const data = (response && response.data) || {};
        UTIL_SERVICE.console("RESULT BROADCAST_RESOLVE_COMPANIES:", data);
        return data;
      }).catch((error) => {
        this.searched = true;
        this.rows = this.selection.map(rowFromSelection);
        this.notFound = [];
        this.notBrazil = [];
        this.shardsFailed = [];

        const status = error && error.response && error.response.status;
        if (isNoCrossToken(error)) {
          this.searchError = 'Saia e entre novamente no Master para autenticar na API v2.';
        } else if (status === 404) {
          this.searchError = 'Endpoint de busca não encontrado (404). A API v2 ainda não está publicada neste ambiente.';
        } else if (status === 401 || status === 403) {
          this.searchError = 'Sem permissão para buscar empresas (' + status + '). É necessário um usuário operador da plataforma.';
        } else {
          this.searchError = 'Não foi possível buscar as empresas' + (status ? ' (' + status + ')' : '') + '.';
        }
        return null;
      }).then((data) => {
        this.resolving = false;
        return data;
      });
    },
    // Grade = resultado novo + quem já estava selecionado; a linha resolvida prevalece sobre a incompleta.
    // Quem a busca encontrou já entra SELECIONADO (quem colou os ids quer anotar neles); a grade serve
    // para desmarcar exceções.
    applySearchResult(data) {
      const items = data.items || [];
      this.rows = mergeRows(this.selection.map(rowFromSelection), items);
      this.selection = applyGridSelection(this.selection, items, items);
      this.notFound = data.notFound || [];
      this.notBrazil = data.notBrazil || [];
      this.shardsFailed = data.shardsFailed || [];
      this.searched = true;
    },
    // Checkbox da grade: marcou entra como 'busca', desmarcou sai; planilha e manual mantêm ação/mensagem.
    onGridSelection(checkedRows) {
      this.selection = applyGridSelection(this.selection, this.rows, checkedRows);
    },
    clearSelection() {
      this.selection = [];
    },
    // Lixeira da grade: sai da seleção, da grade e do campo de busca.
    removeRow(row) {
      const id = String(row.companyId);
      this.selection = this.selection.filter(s => String(s.companyId) !== id);
      this.rows = this.rows.filter(r => String(r.companyId) !== id);
      this.terms = termsWithoutCompany(splitCompanyTerms(this.terms), row).join(', ');
    },
    // Planilha: os ids/CNPJs da coluna `id` vão para a mesma busca da API v2 e as
    // empresas encontradas já entram SELECIONADAS (como na planilha de Avisos).
    onFileSelected(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file || this.resolving) return;

      this.fileError = '';
      this.fileName = file.name;

      readMassShippingCsv(file).then((rows) => {
        if (!rows.length) {
          this.fileError = 'Planilha vazia.';
          return;
        }
        const terms = rows.map(row => String(row.id || '').trim()).filter(id => id.length > 0);
        return this.resolveCompanies(terms).then((data) => {
          if (!data) return;
          // Ação da linha prevalece; linha sem ação fica null e herda a ação padrão na hora do envio.
          const {resolved, unresolved} = resolveCsvRows(rows, data.items || [], this.listAction, null);
          this.selection = mergeSelection(this.selection, resolved);
          this.unresolved = this.unresolved.concat(unresolved);
          this.applySearchResult(data);
        });
      }).catch((error) => {
        this.fileError = error.message || 'Não foi possível ler o arquivo.';
      });
    },
    downloadTemplate() {
      downloadMassShippingTemplate();
    },
    companyName(companyId) {
      const item = this.selection.find(s => s.companyId === companyId);
      return item ? item.companyName : '';
    },
    actionName(id) {
      const action = this.selectableActions.find(a => a.id === id);
      return action ? action.name : id;
    },
    buildItems() {
      const items = [];
      this.selection.forEach(item => {
        this.effectiveActions(item).forEach(action => {
          items.push({companyId: item.companyId, businessNoteType: action.id, msg: item.msg});
        });
      });
      return items;
    },
    send() {
      if (!this.selection.length) return;
      if (this.missingActionCount > 0) {
        this.validate.defaultAction = true;
        return;
      }

      const batches = chunk(this.buildItems(), MASS_SHIPPING_CHUNK_SIZE);
      const aggregate = {created: 0, failed: []};

      this.loadingSend = true;
      this.progress = {current: 0, total: batches.length};

      const sendBatch = (index) => {
        if (index >= batches.length) {
          this.loadingSend = false;
          this.sent = true;
          this.result = aggregate;
          return;
        }

        this.progress.current = index + 1;

        // Cada item já traz o próprio tipo; businessNoteType global fica omitido (backend aceita int?).
        const payload = {
          createdBy: this.createdBy,
          msg: this.defaultMsg || null,
          items: batches[index]
        };

        UTIL_SERVICE.console("URL POST_REPORT_FUNIL:", POST_REPORT_FUNIL, payload);
        REQUEST_SERVICE.post(POST_REPORT_FUNIL, payload).then((response) => {
          UTIL_SERVICE.console("RESULT POST_REPORT_FUNIL:", response.data);
          const data = response.data || {};
          aggregate.created += data.created || 0;
          aggregate.failed = aggregate.failed.concat(data.failed || []);
          sendBatch(index + 1);
        }).catch((reason) => {
          const data = reason && reason.response && reason.response.data;
          const msg = typeof data === 'string' && data ? data : 'Falha na requisição';
          batches[index].forEach(item => aggregate.failed.push({companyId: item.companyId, businessNoteType: item.businessNoteType, reason: msg}));
          if (data && data.erros) ALERT_SERVICE.showMsgError(data.erros);
          sendBatch(index + 1);
        });
      };

      sendBatch(0);
    },
    resetState() {
      this.defaultActionIds = [];
      this.defaultMsg = '';
      this.selection = [];
      this.unresolved = [];
      this.terms = '';
      this.rows = [];
      this.notFound = [];
      this.notBrazil = [];
      this.shardsFailed = [];
      this.searched = false;
      this.searchError = '';
      this.resolving = false;
      this.fileName = '';
      this.fileError = '';
      this.validate.defaultAction = false;
      this.loadingSend = false;
      this.progress = {current: 0, total: 0};
      this.result = null;
      this.sent = false;
      // Força recriação dos inputs filhos (select/textarea guardam estado interno).
      this.resetKey++;
    }
  },
  mounted() {
    $('#massShipping').on('show.bs.modal', () => {
      this.resetState();
    });
  }
}
</script>

<style lang="scss">
#massShipping {
  .mass-shipping__label {
    display: block;
    margin-bottom: 6px;

    span { color: #E6445D; }

    small {
      font-weight: normal;
      color: #777;
    }
  }

  // 3 linhas de checkbox visíveis (22px cada + 2 gaps de 4px + padding 8px + borda); o resto rola.
  .mass-shipping__actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 22px;
    gap: 4px 12px;
    max-height: 92px;
    overflow-y: auto;
    padding: 8px 10px;
    border: 1px solid #E6E9EC;
    border-radius: 4px;

    &.has-error {
      border-color: #E6445D;
    }
  }

  .mass-shipping__terms {
    margin-bottom: 8px;

    input {
      width: 100%;
      height: 36px;
      background: #EEF2F7;
      border: 1px solid #EEF2F7;
      border-radius: 4px !important;
      padding: 6px 12px;
      font-size: 14px;
      color: var(--dark-gray);
      transition: .2s;

      &:hover, &:focus-visible {
        outline: none;
        border-color: #CACACA;
      }
    }
  }

  .mass-shipping__chips {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 10px 0;
    padding: 10px 14px;
    border: 1px solid #d5d7da;
    border-radius: 6px;
    font-size: 13px;

    a {
      color: #1b59f8;
    }
  }

  // Grade com a altura pedida (~400px): só o corpo rola, o rodapé da paginação fica visível.
  .mass-shipping__grid {
    .framed-table__scroll {
      max-height: 400px;
      overflow-y: auto;
    }
  }

  .mass-shipping__action {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-weight: normal;
    font-size: 13px;
    cursor: pointer;

    input[type="checkbox"] {
      margin: 0;
    }
  }

  .mass-shipping__section {
    font-weight: bold;
    margin: 14px 0 8px;
  }

  .mass-shipping__warn {
    color: #E6445D;
    font-weight: normal;
  }

  // Buscar + Planilha lado a lado, mesma altura do input (como em Avisos e Campanhas).
  .mass-shipping__buttons {
    display: flex;
    gap: 8px;
    align-items: stretch;
    margin-bottom: 8px;
  }

  .mass-shipping__btn {
    flex: 1 1 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    margin: 0;
    padding: 0 12px;
    border-radius: 4px !important;
    border: 1px solid #CACACA;
    background: #FFF;
    color: var(--dark-gray);
    font-size: 13px;
    font-weight: normal;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
      opacity: .6;
      cursor: default;
    }

    &--primary {
      background: #1b59f8;
      border-color: #1b59f8;
      color: #FFF;
    }

    // O input de arquivo fica invisível por cima do label inteiro.
    &--upload {
      position: relative;
      overflow: hidden;

      input[type="file"] {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        opacity: 0;
        cursor: pointer;
      }
    }
  }

  .mass-shipping__hint {
    font-size: 12px;
    color: #777;
    margin-bottom: 6px;

    a {
      margin-left: 4px;
      font-weight: 600;
    }
  }

  .mass-shipping__empty {
    color: #777;
    font-size: 13px;
  }

  .mass-shipping__unresolved {
    margin-top: 10px;
    font-size: 13px;

    a {
      margin-left: 8px;
    }

    ul {
      max-height: 120px;
      overflow: auto;
      margin: 6px 0 0;
      padding-left: 18px;
    }
  }

  .mass-shipping__error {
    color: #E6445D;
    font-size: 13px;
  }

  .mass-shipping__progress {
    font-size: 13px;
    padding-top: 10px;
  }

  .mass-shipping__bar {
    height: 6px;
    background: #E6E9EC;
    border-radius: 3px;
    overflow: hidden;
    margin-top: 4px;

    span {
      display: block;
      height: 100%;
      background: #28A745;
      transition: width .2s;
    }
  }

  .mass-shipping__summary ul {
    max-height: 220px;
    overflow: auto;
    font-size: 13px;
  }

}
</style>



// WEBPACK FOOTER //
// src/vue/pages/modals/modalMassShipping.vue